// The indoor unit corner: every Daikin VRV indoor unit, how much ceiling it
// needs, its size in tons and its dimensions, when to use it and its limits.
//
// Phones: a grouped list; tap a unit to open it.
// Laptops: the list on the left, the chosen unit on the right.
// Both: "Compare all 15" opens one table of every unit.
//
// Used on /architects-interior-designers and /daikin-vrv-systems. Data:
// src/data/indoorUnits.js (the pre-render writes the same text for Google).

import { useState } from 'react';
import { Link } from 'react-router-dom';
import UnitDiagram from './UnitDiagram';
import { track } from '../lib/track';
import {
  UNIT_GROUPS, VRV_INDOOR_UNITS, UNIT_RULE, UNIT_SOURCE,
  sizeRows, tonRange, spaceText, spacePill, unitHeightText, planMm,
} from '../data/indoorUnits';

const byGroup = UNIT_GROUPS.map((g) => ({ ...g, units: VRV_INDOOR_UNITS.filter((u) => u.group === g.id) }));
const groupTitle = (id) => (UNIT_GROUPS.find((g) => g.id === id) || {}).title;

function Tick() {
  return <svg viewBox="0 0 24 24" className="mt-0.5 h-4 w-4 shrink-0" fill="none" stroke="#15803d" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20 6L9 17l-5-5" /></svg>;
}
function Warn() {
  return <svg viewBox="0 0 24 24" className="mt-0.5 h-4 w-4 shrink-0" fill="none" stroke="#b45309" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 3l9.5 17h-19z" /><path d="M12 10v4" /><path d="M12 17.5v.01" /></svg>;
}

function Pill({ u, active }) {
  const has = !!u.plan;
  const cls = active ? 'bg-blue-700 text-white'
    : has ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-700';
  return <span className={`shrink-0 whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-bold ${cls}`}>{spacePill(u)}</span>;
}

function Detail({ u, compact, onCheck, variant }) {
  const sizes = sizeRows(u);
  return (
    <div className={compact ? '' : 'rounded-2xl border border-gray-200 p-5 xl:p-6'}>
      {!compact && (
        <>
          <div className="flex flex-wrap items-center gap-2.5">
            <h3 className="text-2xl font-extrabold tracking-tight text-gray-900">{u.name}</h3>
            <span className="rounded-full bg-blue-50 px-2.5 py-1 text-xs font-bold text-blue-900">Daikin {u.series}</span>
          </div>
          <p className="mt-1 text-[13px] text-gray-500">{groupTitle(u.group)}</p>
        </>
      )}

      <div className={`grid gap-4 ${compact ? '' : 'mt-4 xl:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)]'}`}>
        <div className="self-start rounded-xl border border-slate-200 bg-slate-50 p-2">
          <UnitDiagram unit={u} />
        </div>
        <div className="flex flex-col gap-2">
          <div className="grid grid-cols-2 gap-2">
            <div className="rounded-xl bg-gray-50 p-3">
              <p className="text-[10px] font-bold tracking-wider text-gray-500">CAPACITY</p>
              <p className="mt-1 text-lg font-extrabold text-gray-900">{tonRange(u)}</p>
            </div>
            <div className="rounded-xl bg-orange-50 p-3">
              <p className="text-[10px] font-bold tracking-wider text-orange-800">CEILING TO PLAN FOR</p>
              <p className="mt-1 text-lg font-extrabold leading-tight text-orange-800">{u.plan ? spacePill(u) : (u.box ? '9 in box' : (u.room ? 'AHU room' : 'None'))}</p>
              <p className="mt-0.5 text-[11px] leading-snug text-orange-800">
                {u.plan ? `${planMm(u)[0]}–${planMm(u)[1]} mm${u.ducts ? ', plus ducts' : ''}. Includes room to service it.` : spaceText(u)}
              </p>
            </div>
          </div>
          <div className="rounded-xl bg-gray-50 p-3">
            <p className="text-[10px] font-bold tracking-wider text-gray-500">UNIT SIZE (H × W × D)</p>
            {u.dims.map((d) => <p key={d} className="mt-1 text-[13px] font-semibold leading-snug text-gray-900">{d}</p>)}
            {u.unitMm && (
              <p className="mt-1 text-[11px] leading-snug text-gray-600">Unit height {unitHeightText(u)}{u.unitNote ? `: ${u.unitNote}` : ''}</p>
            )}
          </div>
          <div className="rounded-xl bg-gray-50 p-3">
            <p className="text-[10px] font-bold tracking-wider text-gray-500">WHAT SHOWS IN THE ROOM</p>
            <p className="mt-1 text-sm font-semibold text-gray-900">{u.shows}</p>
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-xs font-bold text-gray-700">Best for</span>
            {u.best.map((b) => <span key={b} className="rounded-full bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-900">{b}</span>)}
          </div>
        </div>
      </div>

      <div className="mt-4 grid gap-3 md:grid-cols-2">
        <div className="rounded-xl bg-green-50 p-4">
          <p className="text-sm font-extrabold text-green-800">Use it when</p>
          <ul className="mt-2 flex flex-col gap-1.5">
            {u.use.map((x) => <li key={x} className="flex gap-2 text-sm leading-snug text-gray-800"><Tick />{x}</li>)}
          </ul>
        </div>
        <div className="rounded-xl bg-amber-50 p-4">
          <p className="text-sm font-extrabold text-amber-900">Limitations</p>
          <ul className="mt-2 flex flex-col gap-1.5">
            {u.limits.map((x) => <li key={x} className="flex gap-2 text-sm leading-snug text-gray-800"><Warn />{x}</li>)}
          </ul>
        </div>
      </div>

      <div className="mt-4 grid gap-4 md:grid-cols-2">
        <div>
          <p className="text-xs font-bold uppercase tracking-wider text-gray-500">Sizes</p>
          <table className="mt-1.5 w-full text-left text-[13px]">
            <thead className="text-[11px] text-gray-500">
              <tr><th scope="col" className="py-1 pr-2 font-semibold">Tons</th><th scope="col" className="py-1 pr-2 font-semibold">Btu/h</th><th scope="col" className="py-1 font-semibold">Model</th></tr>
            </thead>
            <tbody>
              {sizes.map((s) => (
                <tr key={s.model} className="border-t border-gray-100">
                  <td className="py-1 pr-2 font-bold text-gray-900">{s.tr} TR</td>
                  <td className="py-1 pr-2 text-gray-700">{s.btuText}</td>
                  <td className="py-1 text-[11px] text-gray-500">{s.model}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {u.specs.length > 0 && (
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-gray-500">Details</p>
            <dl className="mt-1.5 flex flex-col gap-1.5">
              {u.specs.map(([k, v]) => (
                <div key={k} className="grid grid-cols-[104px_1fr] gap-2 border-t border-gray-100 pt-1.5 text-[13px] leading-snug">
                  <dt className="text-gray-500">{k}</dt><dd className="text-gray-900">{v}</dd>
                </div>
              ))}
            </dl>
          </div>
        )}
      </div>

      <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2">
        {u.plan && onCheck && (
          <button type="button" onClick={() => onCheck(u.id)} className="text-sm font-bold text-blue-700 hover:underline">Check your ceiling with this unit ›</button>
        )}
        {variant === 'vrv' ? (
          <Link to="/architects-interior-designers#pipes" className="text-sm font-bold text-blue-700 hover:underline">Pipe space and installation, for designers ›</Link>
        ) : (
          <Link to="/daikin-vrv-systems#vrv-contact" className="text-sm font-bold text-blue-700 hover:underline">See VRV at our experience centre ›</Link>
        )}
      </div>
    </div>
  );
}

export default function IndoorUnitCorner({ initial = 'one-way-cassette', onCheck, variant = 'designers' }) {
  const [sel, setSel] = useState(initial);
  const [open, setOpen] = useState(initial);
  const selected = VRV_INDOOR_UNITS.find((u) => u.id === sel) || VRV_INDOOR_UNITS[0];

  const pick = (id) => { setSel(id); track('indoor_unit_view', { unit: id, page: variant }); };
  const toggle = (id) => {
    setOpen((o) => (o === id ? '' : id));
    if (open !== id) track('indoor_unit_view', { unit: id, page: variant });
  };

  return (
    <div>
      {/* Phones and tablets: tap to open */}
      <div className="flex flex-col gap-5 lg:hidden">
        {byGroup.map((g) => (
          <div key={g.id}>
            <p className="text-sm font-extrabold text-gray-900">{g.title}</p>
            <p className="text-xs text-gray-500">{g.sub}</p>
            <div className="mt-2 overflow-hidden rounded-2xl border border-gray-200">
              {g.units.map((u) => {
                const isOpen = open === u.id;
                return (
                  <div key={u.id} className="border-b border-gray-100 last:border-b-0">
                    <button type="button" onClick={() => toggle(u.id)} aria-expanded={isOpen} aria-controls={`unit-${u.id}`}
                      className="flex min-h-[60px] w-full items-center gap-2.5 bg-white px-3 py-2.5 text-left">
                      <span className="min-w-0 flex-1">
                        <span className="block text-[15px] font-bold text-gray-900">{u.name}</span>
                        <span className="mt-0.5 block text-xs text-gray-500">{u.series} · {tonRange(u)}</span>
                      </span>
                      <Pill u={u} />
                      <svg viewBox="0 0 24 24" className={`h-[18px] w-[18px] shrink-0 transition-transform ${isOpen ? 'rotate-180 text-blue-700' : 'text-gray-400'}`} fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M6 9l6 6 6-6" /></svg>
                    </button>
                    {isOpen && (
                      <div id={`unit-${u.id}`} className="px-3 pb-4">
                        <Detail u={u} compact onCheck={onCheck} variant={variant} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Laptops: list and detail side by side */}
      <div className="hidden gap-6 lg:grid lg:grid-cols-[320px_minmax(0,1fr)] xl:grid-cols-[340px_minmax(0,1fr)]">
        <div className="self-start rounded-2xl border border-gray-200 p-2.5" role="listbox" aria-label="Indoor units">
          {byGroup.map((g) => (
            <div key={g.id}>
              <p className="px-2 pb-1 pt-2.5 text-[11px] font-extrabold uppercase tracking-wider text-gray-500">{g.title}</p>
              {g.units.map((u) => {
                const on = u.id === selected.id;
                return (
                  <button key={u.id} type="button" role="option" aria-selected={on} onClick={() => pick(u.id)}
                    className={`flex w-full items-center gap-2.5 rounded-xl border-[1.5px] px-2.5 py-2 text-left ${on ? 'border-blue-700 bg-blue-50' : 'border-transparent hover:bg-gray-50'}`}>
                    <span className="min-w-0 flex-1">
                      <span className={`block text-sm ${on ? 'font-extrabold text-blue-900' : 'font-bold text-gray-900'}`}>{u.name}</span>
                      <span className={`mt-px block text-xs ${on ? 'text-blue-800' : 'text-gray-500'}`}>{u.series} · {tonRange(u)}</span>
                    </span>
                    <Pill u={u} active={on} />
                  </button>
                );
              })}
            </div>
          ))}
        </div>
        <Detail u={selected} onCheck={onCheck} variant={variant} />
      </div>

      <p className="mt-4 text-xs leading-relaxed text-gray-500">{UNIT_RULE} {UNIT_SOURCE}</p>

      <details className="group mt-3 rounded-2xl border border-gray-200">
        <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-4 py-3.5 text-[15px] font-bold text-blue-700 [&::-webkit-details-marker]:hidden">
          Compare all 15 in one table
          <svg viewBox="0 0 20 20" className="h-4 w-4 shrink-0 transition-transform group-open:rotate-180" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d="M5 8l5 5 5-5" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </summary>
        <div className="overflow-x-auto px-4 pb-4">
          <table className="w-full min-w-[860px] border-collapse text-left text-[13px]">
            <thead className="bg-gray-50">
              <tr>
                {['Indoor unit', 'Daikin', 'Tons', 'Ceiling to plan for', 'Unit size (H × W × D)', 'What shows', 'Main limit'].map((h) => (
                  <th key={h} scope="col" className="px-2.5 py-2 font-semibold text-gray-900">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {VRV_INDOOR_UNITS.map((u) => (
                <tr key={u.id} className="border-t border-gray-100 align-top">
                  <th scope="row" className="px-2.5 py-2 font-bold text-gray-900">{u.name}</th>
                  <td className="px-2.5 py-2 text-gray-700">{u.series}</td>
                  <td className="whitespace-nowrap px-2.5 py-2 text-gray-700">{tonRange(u)}</td>
                  <td className="px-2.5 py-2 text-gray-700">{spaceText(u)}</td>
                  <td className="px-2.5 py-2 text-gray-700">{u.dims[0]}</td>
                  <td className="px-2.5 py-2 text-gray-700">{u.shows}</td>
                  <td className="px-2.5 py-2 text-gray-700">{u.limits[0]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </div>
  );
}
